import React from "react";
import { useEffect, useState } from "react";
import "./Bills.css";
import BillList from "../../components/BillList/BillList";
import Loading from "../Loading";
import { Stack, Typography, Box, Input, Button, IconButton } from "@mui/joy";
import SearchIcon from "@mui/icons-material/Search";
import ClearIcon from "@mui/icons-material/Clear";
import LoadMoreButton from "../../components/LoadMoreButton/LoadMoreButton";
import SEOHead from "../../components/SEOHead/SEOHead";
import seoConfig from "../../utils/seoConfig";
import HandsRaiseImg from "../../hands-raise.jpg";

function Bills() {
  const [bills, setBills] = useState([]);
  const [lastKey, setLastKey] = useState(null);
  const [loading, setLoading] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [searchParams, setSearchParams] = useState({
    keyword: "",
    state: 'FD'
  });

  const fetchBills = async (reset = false) => {
    if (loading || (!hasMore && !reset)) return;
    setLoading(true);
    if (reset) setBills([]);

    try {
      await fetch(
        "/api/bill?" +
          new URLSearchParams({
            keyword: searchParams.keyword,
            state: searchParams.state,
            limit: 20,
            lastKey: reset ? null : JSON.stringify(lastKey),
          }).toString()
      )
        .then((response) => response.json())
        .then((data) => {
          const { items, lastKey: newLastKey } = data;
          setBills((prevBills) => (reset ? items : [...prevBills, ...items]));
          setLastKey(newLastKey);
          setHasMore(!!newLastKey);
        });
    } catch (err) {
      console.error("Error fetching bills:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    fetchBills(true);
  };

  const handleLoadMore = () => {
    fetchBills(false);
  };

  const clearSearch = () => {
    setSearchParams({ ...searchParams, keyword: "" });
    fetchBills(true);
  };

  useEffect(() => {
    document.title = seoConfig.pages.bills.title;
    fetchBills();
  }, []);

  return (
    <>
      <SEOHead
        title={seoConfig.pages.bills.title}
        description={seoConfig.pages.bills.description}
        keywords={seoConfig.pages.bills.keywords}
        path={seoConfig.pages.bills.path}
        structuredData={[
          seoConfig.getBreadcrumbs('/bills', 'Bills & Legislation')
        ]}
      />

      {/* background-attachment: fixed keeps the crop locked as content grows */}
      <Box
        sx={{
          position: "relative",
          minHeight: "100vh",
          backgroundColor: "#003049",
          backgroundImage: `linear-gradient(rgba(0,48,73,0.8), rgba(0,48,73,0.8)), url(${HandsRaiseImg})`,
          backgroundSize: "100% 100%, cover",
          backgroundPosition: "0 0, center",
          backgroundAttachment: "scroll, fixed",
        }}
      >
        <Stack
          direction="column"
          sx={{ position: "relative", zIndex: 1, alignItems: "stretch" }}
        >
          {loading && bills.length === 0 ? (
            <Loading />
          ) : (
            <>
              {/* Dark header with search */}
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  px: { xs: "1.25rem", md: "calc(2rem + 10px)" },
                  py: { xs: "2.5rem", md: "2.5rem" },
                }}
              >
                <Box sx={{ maxWidth: "1200px", width: "100%" }}>
                  <Stack spacing={2} sx={{ mb: "2rem" }}>
                    <Typography
                      component="p"
                      sx={{
                        fontSize: "16px",
                        fontWeight: 600,
                        color: "var(--ce-dark-eyebrow)",
                        textTransform: "uppercase",
                        letterSpacing: "0.05em",
                      }}
                    >
                      Federal Legislation
                    </Typography>
                    <Typography
                      level="h1"
                      sx={{
                        color: "white",
                        fontSize: { xs: "2.25rem", md: "3.75rem" },
                        fontWeight: 700,
                        lineHeight: 1.2,
                      }}
                    >
                      Bills &amp; Legislation
                    </Typography>
                    <Typography
                      sx={{
                        color: "white",
                        fontSize: "1.125rem",
                        lineHeight: 1.65,
                      }}
                    >
                      Plain-language summaries of active federal legislation. Search by topic or browse all.
                    </Typography>
                  </Stack>

                  <Box
                    component="form"
                    onSubmit={handleSearch}
                    sx={{ display: "flex", gap: 1.5, width: "100%" }}
                  >
                    <Input
                      placeholder="Search Legislation"
                      startDecorator={<SearchIcon sx={{ color: "#9ca3af" }} />}
                      value={searchParams.keyword}
                      onChange={(e) =>
                        setSearchParams({ ...searchParams, keyword: e.target.value })
                      }
                      endDecorator={
                        searchParams.keyword ? (
                          <IconButton
                            size="sm"
                            variant="plain"
                            onClick={clearSearch}
                            aria-label="Clear search"
                            sx={{ color: "#9ca3af" }}
                          >
                            <ClearIcon />
                          </IconButton>
                        ) : null
                      }
                      sx={{
                        flexGrow: 1,
                        backgroundColor: "white",
                        border: "1px solid rgba(0,0,0,0.23)",
                        borderRadius: "4px",
                        fontSize: "16px",
                        "--Input-focusedHighlight": "var(--ce-accent)",
                        "&:focus-within": {
                          borderColor: "var(--ce-accent)",
                          boxShadow: "0 0 0 3px rgba(0,117,178,0.15)",
                        },
                      }}
                    />
                    <Button
                      type="submit"
                      sx={{
                        backgroundColor: "var(--ce-accent)",
                        color: "white",
                        px: 3,
                        fontSize: "16px",
                        fontWeight: 600,
                        borderRadius: "4px",
                        flexShrink: 0,
                        minHeight: "48px",
                        transition: "background-color 0.15s ease",
                        "&:hover": { backgroundColor: "var(--ce-accent-hover)" },
                        "@media (prefers-reduced-motion: reduce)": { transition: "none" },
                      }}
                    >
                      Search
                    </Button>
                  </Box>
                </Box>
              </Box>

              {/* Card grid */}
              <Box sx={{ flex: 1 }}>
                <BillList
                  bills={bills}
                  loading={loading}
                  hasMore={hasMore}
                  searchParams={searchParams}
                  onClearSearch={clearSearch}
                />
                <LoadMoreButton
                  onLoadMore={handleLoadMore}
                  loading={loading}
                  hasMore={hasMore}
                  currentCount={bills.length}
                  itemType="bills"
                />
              </Box>
            </>
          )}
        </Stack>
      </Box>
    </>
  );
}

export default Bills;
